import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import { Funnel } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { Button } from "@/components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "@/hooks/useAuth";
import StaffTableSkeleton from "@/components/skeleton/Staff-skeletion";
import { CardContent, Card } from "@/components/ui/card";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { format, parseISO, parse } from "date-fns";
import { fetchBookingById, clearBookingDetails } from "@/redux/slice/table-slice/table-slice";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Tabs, TabsTrigger, TabsList } from "@/components/ui/tabs";
import BookingDetailsTabs from "@/components/table/table-history/booking-details-tabs";
import OrderDetailsTabs from "@/components/table/table-history/order-details-tabs";
import { getSingleOrderApi } from "@/api/take-order/take-order";

function TableBookingHistory() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cencelDialog, setCencelDialog] = useState(false);
  const { user } = useAuth();
  const { id } = useParams();
  const {
    singleBooking,
    singleBookingLoading,
  } = useSelector((state) => state.table.table);

  const [activeTab, setActiveTab] = useState("booking-details");
  const [localOrder, setLocalOrder] = useState(null);
  const [localOrderLoading, setLocalOrderLoading] = useState(false);

  useEffect(() => {
    dispatch(clearBookingDetails());
    setLocalOrder(null);
    setLocalOrderLoading(false);
    setActiveTab("booking-details");
    if (id) {
      dispatch(fetchBookingById(id));
    }
  }, [id, dispatch]);

  const fetchOrderDetailsLocally = async (orderId) => {
    if (!orderId) return;
    setLocalOrderLoading(true);
    try {
      const res = await getSingleOrderApi(orderId);
      if (res?.status === "success") {
        setLocalOrder(res.data);
      } else {
        setLocalOrder(null);
      }
    } catch (e) {
      setLocalOrder(null);
    } finally {
      setLocalOrderLoading(false);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === "order-details" && !localOrder && !localOrderLoading) {
      fetchOrderDetailsLocally(singleBooking?.order_id);
    }
  };

  const [isAssignTableModalOpen, setIsAssignTableModalOpen] = useState(false);
  const closeAssignTableModal = () => {
    setIsAssignTableModalOpen(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);

    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");

    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });

    return `${yyyy}-${mm}-${dd} ${time}`;
  };

  return (
    <Layout2
      pageName={{
        icon: true,
        title: singleBookingLoading ? "Booking Details" : `Booking ID: ${singleBooking?.id || ""}`,
        backPath: "/table?tab=history",
      }}
      paths={[
        { name: "Home", path: "/home" },
        {
          name: "Booking History",
          path: "/table?tab=history",
        },
        {
          name: "Booking Details",
        },
      ]}
    >
      <div className="container mt-5 px-4 sm:px-6">
        {singleBookingLoading ? (
          <BookingSkeleton />
        ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="bg-cblack2 border-b border-gray-800 mb-6 w-full flex justify-start gap-2 h-12 p-1 rounded-t-lg">
              <TabsTrigger 
                value="booking-details"
                className="px-6 py-2 data-[state=active]:bg-cblack3 data-[state=active]:text-cyellow transition-all"
              >
                Booking Details
              </TabsTrigger>
              <TabsTrigger 
                value="order-details"
                className="px-6 py-2 data-[state=active]:bg-cblack3 data-[state=active]:text-cyellow transition-all"
              >
                Order Details
              </TabsTrigger>
            </TabsList>
            <div className="mt-2">
              <BookingDetailsTabs
                singleBooking={singleBooking}
                formatDateTime={formatDateTime}
              />
              <OrderDetailsTabs
                singleOrder={localOrder}
                singleOrderLoading={localOrderLoading}
              />
            </div>
          </Tabs>
        )}
      </div>
    </Layout2>
  );
}

const BookingSkeleton = () => (
  <div className="flex flex-col mt-2 pb-10 animate-pulse">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Customer Info Skeleton */}
      <div className="flex flex-col bg-cblack5 p-3 space-y-2">
        <div className="h-6 w-32 bg-neutral-800 rounded mb-2"></div>
        <Card className="bg-cblack5 border-gray-800">
          <CardContent className="pt-3 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-4 w-32 bg-neutral-800 rounded"></div>
                <div className="h-4 w-48 bg-neutral-800 rounded"></div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Booking Info Skeleton */}
      <div className="flex flex-col bg-cblack5 p-3 space-y-2">
        <div className="h-6 w-32 bg-neutral-800 rounded mb-2"></div>
        <Card className="bg-cblack5 border-gray-800">
          <CardContent className="pt-3 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="h-4 w-32 bg-neutral-800 rounded"></div>
                <div className="h-4 w-48 bg-neutral-800 rounded"></div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>

    {/* Table / Staff Details Skeletons */}
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {[...Array(3)].map((_, idx) => (
        <div key={idx} className="space-y-3 bg-cblack5 p-3">
          <div className="h-5 w-28 bg-neutral-800 rounded"></div>
          <Card className="bg-cblack5 border-gray-800">
            <CardContent className="pt-3">
              <div className="h-10 w-full bg-neutral-800 rounded"></div>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  </div>
);

export default TableBookingHistory;
